import type { Hex } from '@/store/ids';

/**
 * The record shapes stored in IndexedDB.
 *
 * Two conventions run through all of them.
 *
 * Sequence numbers are stored as JavaScript numbers, not bigints. IndexedDB
 * rejects a bigint as a key, and every keyset query in Section 14 pages on a
 * conversation sequence, so a bigint field would have to be shadowed by a
 * number anyway. The server column is BIGINT with CHECK >= 0 and Section 13
 * already bounds protobuf uint64 values to 2^63-1 for the PostgreSQL backend;
 * this client additionally assumes sequences stay inside 2^53-1. That is
 * roughly nine quadrillion messages in one conversation, so the assumption is
 * safe, but it is an assumption and it is written down here rather than buried.
 *
 * Identifiers are lowercase hexadecimal strings. See `store/ids.ts`.
 */

/** `chat.v1.Content`, flattened to a form IndexedDB can store and index. */
export interface StoredEntity {
  kind: number;
  offset: number;
  length: number;
  value: string | null;
}

export interface StoredAttachment {
  mediaId: Hex;
  caption: string | null;
  filename: string | null;
  mime: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
}

export interface StoredContent {
  kind: number;
  text: string;
  entities: StoredEntity[];
  attachments: StoredAttachment[];
  replyTo: Hex | null;
  /** Group topic this message belongs to, from Phase 3. */
  topicId: Hex | null;
  /** Poll, checklist or other record created atomically with the message. */
  embeddedId: Hex | null;
}

/** `chat.v1.Origin`: where a forwarded message came from, when disclosure is permitted. */
export interface StoredOrigin {
  conversationId: Hex | null;
  messageId: Hex | null;
  displayName: string;
}

export interface AccountRecord {
  /** User identifier. */
  id: Hex;
  deviceId: Hex;
  sessionId: Hex;
  /**
   * Stable across sign-outs on this browser profile. The server binds a device
   * row to it, which is what stops a re-registration from being treated as a
   * brand new device and replaying operations under a fresh identity.
   */
  installationId: string;
  displayName: string;
  username: string | null;
  phoneE164: string;
  lastSignedInAt: number;
}

export interface ConversationRecord {
  id: Hex;
  kind: number;
  title: string;
  /** Highest server sequence known to exist, from dialog heads or events. */
  headSeq: number;
  /** Highest sequence this account has read, anywhere. */
  lastReadSeq: number;
  /** Peer read and delivery positions, for the outgoing status indicators. */
  peerReadSeq: number;
  peerDeliveredSeq: number;
  unreadCount: number;
  unreadCapped: boolean;
  policyVersion: number;
  historyPolicy: number;
  deliveryMode: number;
  ownRole: string;
  /** Sort key for the inbox: the local time of the newest known activity. */
  updatedAt: number;
  lastMessagePreview: string;
  lastMessageAt: number;
  /**
   * True while this row stands in for a conversation whose creation has not
   * been committed by the server yet. The authoritative identifier replaces it
   * during reconciliation.
   */
  provisional: boolean;
  /** Set when the server has revoked access; the row is kept for reconciliation. */
  accessRevoked: boolean;

  // --- Phase 3 and 4 policy projections -----------------------------------
  /** Section 06 slow mode: 0, 10, 30 or 60 seconds between member sends. */
  slowModeSeconds: number;
  /** Forwarding, copying and saving are refused locally when true. */
  protectedContent: boolean;
  /** Present only for a public group or channel. */
  publicUsername: string | null;
  /** Channel only: the group that carries its comments, when one is linked. */
  linkedDiscussionId: Hex | null;
  /**
   * The conversation photo, or null when none is set.
   *
   * Null means draw generated initials. It is deliberately not a URL: the bytes
   * need a short-lived authorized grant, which the interface requests separately
   * and which expires, so a stored URL would go stale in the database.
   */
  photoMediaId: Hex | null;
  /**
   * Channel notification choice. Section 06 defaults channels to
   * mentions/replies only; a user may explicitly choose every post.
   */
  notificationMode: NotificationMode;
  /** True while this account holds an active channel subscription. */
  subscribed: boolean;
  /**
   * Local wall-clock time of this account's most recent send, used to enforce
   * slow mode in the composer. It is advisory only — the server enforces the
   * real limit and its clock is the authority.
   */
  lastOwnSendAt: number;
}

export type NotificationMode = 'MENTIONS' | 'ALL' | 'NONE';

/** How a locally originated message is progressing, as the user sees it. */
export type MessageLocalState =
  | 'PENDING'
  | 'COMMITTED'
  | 'FAILED';

export interface MessageRecord {
  conversationId: Hex;
  /** Authoritative message identifier, or the temporary identifier until commit. */
  id: string;
  /**
   * Ordering position within the conversation.
   *
   * For a committed message this is the server creation sequence. A message
   * that has not committed yet has no server sequence, so it is placed after
   * every committed message using `PENDING_ORDER_BASE`. The timeline reads this
   * index and never re-sorts by timestamp, which is what Section 14 means by
   * "ordering uses server creation sequence".
   */
  orderSeq: number;
  /** Server creation sequence, or 0 while pending. */
  createdSeq: number;
  /**
   * The account this message is displayed as, or null when it is displayed as a
   * channel. Prefer `sender`: a channel-origin message leaves this null, because
   * a channel identifier in an account field would name an account that does not
   * exist, and the publishing administrator's identifier there would be a
   * privacy leak.
   */
  senderId: Hex | null;
  /**
   * Who the message is displayed as, typed. Present on everything a 1.1 server
   * sends.
   */
  sender: StoredSender | null;
  version: number;
  content: StoredContent;
  createdAt: number;
  editedAt: number | null;
  deleted: boolean;
  /** True when this account sent it; drives which status indicator is shown. */
  outgoing: boolean;
  localState: MessageLocalState;
  /** The outbox operation that produced it, while one is still outstanding. */
  opId: Hex | null;
  /**
   * The temporary identifier this message was first rendered under. It is kept
   * after reconciliation so a duplicate delivery of the same commit maps onto
   * the existing bubble instead of creating a second one.
   */
  tempId: string | null;
  /** Present only when the origin permits attribution; see Section 06. */
  forwardOrigin: StoredOrigin | null;
  /**
   * Channel view count. Section 06 requires this to be presented as
   * approximate and never as a delivery or read receipt.
   */
  viewCount: number;
  /** True while this message is pinned in its conversation. */
  pinned: boolean;
}

/**
 * Pending messages sort after every committed one.
 *
 * 2^49 is far above any reachable server sequence and far below 2^53, so adding
 * a millisecond timestamp to it stays exact in a double.
 */
export const PENDING_ORDER_BASE = 2 ** 49;

export interface EventSeenRecord {
  streamId: string;
  eventId: Hex;
  seq: number;
  seenAt: number;
}

export interface StreamCursorRecord {
  streamId: string;
  /** Opaque server cursor. Stored as bytes; it is never parsed by the client. */
  cursor: Uint8Array | null;
  /** Contiguous frontier. An event past this triggers durable gap repair. */
  scannedThrough: number;
  head: number;
  updatedAt: number;
}

export type OutboxState = 'QUEUED' | 'SENDING' | 'COMMITTED' | 'FAILED';

export interface OutboxError {
  code: string;
  message: string;
  retryable: boolean;
  retryAfterMs: number | null;
  currentVersion: number | null;
  at: number;
}

/**
 * The Section 14 outbox record, field for field.
 *
 * `normalizedPayload` is the canonical business input the payload hash is taken
 * over, kept rather than re-derived so that a retry after a code change still
 * sends byte-identical intent under the same operation identifier.
 */
export interface OutboxRecord {
  opId: Hex;
  originDeviceId: Hex;
  conversationId: Hex;
  methodId: number;
  normalizedPayload: NormalizedPayload;
  payloadHash: Hex;
  /**
   * Attachment metadata for a content-carrying operation.
   *
   * Kept beside the normalized payload rather than inside it because it is not
   * part of the hashed intent: only the media identifiers are. Size, MIME,
   * dimensions and duration describe the object, and the capability token is a
   * short-lived grant that a retry has to re-obtain rather than replay.
   */
  attachments: StoredAttachment[];
  tempId: string;
  state: OutboxState;
  attemptCount: number;
  nextAttemptAt: number;
  lastError: OutboxError | null;
  createdAt: number;
  lastAttemptAt: number | null;
}

/**
 * Canonical business input for a mutation.
 *
 * It is plain JSON on purpose: Section 13 hashes RFC 8785 canonical JSON of the
 * normalized fields, so the value that is hashed and the value that is stored
 * have to be the same thing.
 */
export type NormalizedPayload = { readonly [key: string]: JsonValue };

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | readonly JsonValue[]
  | { readonly [key: string]: JsonValue };

export interface DraftRecord {
  conversationId: Hex;
  text: string;
  /** Message being replied to, kept attached until removed or sent. */
  replyTo: string | null;
  replyToName: string | null;
  updatedAt: number;
  /**
   * Server draft version this local copy was derived from. `SetDraft` carries
   * it as `expected_version`; a mismatch is what surfaces the conflict below
   * rather than silently overwriting another device's text.
   */
  version: number;
  /** Uploads referenced by the draft that are not READY yet (Section 15). */
  pendingUploadIds: Hex[];
  /** Topic the draft is addressed to, in a group that uses topics. */
  topicId: Hex | null;
  /**
   * The remote copy retained after a version conflict.
   *
   * Section 06: "Conflicts retain both local and remote copies and present a
   * choose-version action." Neither side is discarded until the user picks.
   */
  conflict: DraftConflict | null;
}

export interface DraftConflict {
  text: string;
  version: number;
  updatedAt: number;
}

/**
 * Attachment upload lifecycle, from Section 06.
 *
 * CREATED → UPLOADING → VERIFYING → READY, with FAILED, EXPIRED and CANCELLED
 * as terminal alternatives. PAUSED is a local state only: the server has no
 * opinion about a transfer the client has stopped feeding.
 */
export type UploadState =
  | 'CREATED'
  | 'UPLOADING'
  | 'PAUSED'
  | 'VERIFYING'
  | 'READY'
  | 'FAILED'
  | 'EXPIRED'
  | 'CANCELLED';

/** One part the server has confirmed it holds. Mirrors `chat.v1.UploadedPart`. */
export interface UploadPartRecord {
  partNumber: number;
  sizeBytes: number;
  checksum: string;
  etag: string;
}

export interface UploadRecord {
  /**
   * The client's own identifier for this transfer, and the primary key.
   *
   * It exists before `CreateUpload` has been answered, which is the whole
   * point: a draft can reference the attachment, the interface can show a
   * progress row, and the transfer can be resumed after a reload, all while the
   * server-issued `upload_id` is still absent. The server identifier is stored
   * separately rather than replacing this one, so nothing that already points
   * at the transfer has to be rewritten when it arrives.
   */
  uploadId: Hex;
  serverUploadId: Hex | null;
  mediaId: Hex | null;
  conversationId: Hex;
  filename: string;
  mime: string;
  /** Section 15 `media_kind`: photo, video, file, voice or video_note. */
  mediaKind: string;
  sizeBytes: number;
  sha256: Hex;
  /** Section 06 fixes 8 MiB parts; the server confirms the size it will accept. */
  partSize: number;
  state: UploadState;
  /**
   * Confirmed uploaded bytes.
   *
   * Section 06: progress "counts confirmed uploaded bytes, not only bytes
   * written to a socket". This field is only ever advanced from the part list
   * the server acknowledged, never from a request body that was handed to the
   * network stack.
   */
  bytesConfirmed: number;
  parts: UploadPartRecord[];
  expiresAt: number;
  /**
   * Stable operation identifiers for the three mutating upload methods.
   *
   * They are generated once, when the transfer is created, and reused on every
   * attempt. That is what makes a retried `CreateUpload` return the existing
   * upload rather than allocating a second object key for the same bytes.
   */
  createOpId: Hex;
  completeOpId: Hex;
  cancelOpId: Hex;
  lastError: string | null;
  /**
   * The bytes being uploaded.
   *
   * Held in IndexedDB rather than in memory so that a reload, a crash or a
   * closed tab does not lose a transfer the user believes is in progress. It is
   * cleared once the object is READY, because at that point the server holds
   * the authoritative copy and keeping a second one only consumes the origin's
   * storage quota.
   */
  blob: Blob | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  /** Locally computed preview samples for a voice draft; see `media/waveform`. */
  waveform: number[] | null;
  createdAt: number;
  updatedAt: number;
}

/** Server-side media state, projected from `MediaInfo` and MEDIA_READY events. */
export interface MediaVariantRecord {
  name: string;
  mime: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  /** Section 14: 100 normalized samples, produced by the media worker. */
  waveform: number[];
}

export interface MediaRecord {
  mediaId: Hex;
  state: string;
  mime: string;
  sizeBytes: number;
  variants: MediaVariantRecord[];
  failureCode: string | null;
  updatedAt: number;
}

export interface DownloadPartRecord {
  mediaId: Hex;
  variant: string;
  offset: number;
  length: number;
  bytes: Uint8Array;
}

/**
 * One user's one reaction to one message.
 *
 * Section 06 keeps exact source rows and projects counts asynchronously, so the
 * client stores rows too and derives counts from them. Storing counts alone
 * would make "did I react?" unanswerable offline.
 */
export interface ReactionRecord {
  conversationId: Hex;
  messageId: string;
  userId: Hex;
  reaction: string;
  active: boolean;
  version: number;
}

export interface PinRecord {
  conversationId: Hex;
  messageId: string;
  pinnedAt: number;
}

export interface MemberRecord {
  conversationId: Hex;
  userId: Hex;
  /** `chat.v1.Member.Role` numeric value. */
  role: number;
  /** Admin permission flags; see `protocol/permissions.ts`. */
  permissions: number;
  joinedSeq: number;
  banned: boolean;
  /** Filled in from profiles when known; the mention picker needs a name. */
  displayName: string;
  username: string | null;
}

export interface TopicRecord {
  conversationId: Hex;
  topicId: Hex;
  title: string;
  closed: boolean;
  deleted: boolean;
  version: number;
}

export interface PollOptionRecord {
  id: Hex;
  text: string;
  votes: number;
}

export interface PollRecord {
  pollId: Hex;
  conversationId: Hex;
  question: string;
  options: PollOptionRecord[];
  multiple: boolean;
  anonymous: boolean;
  closed: boolean;
  version: number;
  myVotes: Hex[];
  /**
   * Only ever present when the server chose to disclose it. Section 06 returns
   * the correct option to the creator, to a voter who has voted, or after
   * closure; the client never infers it and never guesses.
   */
  correctOption: Hex | null;
  explanation: string | null;
}

/** A group invitation this account created, kept so it can be shown and revoked. */
export interface InviteRecord {
  conversationId: Hex;
  token: string;
  createdAt: number;
  expiresAt: number;
  maxUses: number;
  approval: boolean;
}

/**
 * Where a provisional conversation identifier ended up.
 *
 * A conversation created locally gets a provisional identifier so the user can
 * open it and type immediately. When the server answers, the real identifier
 * arrives and the provisional row is re-keyed to it. Anything still holding the
 * old identifier — most importantly the address bar, which is where the user
 * was sent the moment they created it — would otherwise be pointing at a row
 * that no longer exists, and the conversation would simply vanish in front of
 * them.
 *
 * This makes the rename something the interface can follow rather than
 * something it has to be told about out of band.
 */
export interface ConversationAliasRecord {
  provisionalId: Hex;
  canonicalId: Hex;
  createdAt: number;
}

export interface PreferenceRecord {
  key: string;
  value: JsonValue;
}

export interface SchemaMetadataRecord {
  version: number;
  appliedAt: number;
}

/**
 * The identity a message is displayed under.
 *
 * `kind` is what makes this safe to cache: a user and a channel can hold the
 * same UUID without being the same subject, so an entry keyed on the identifier
 * alone would collide them. `profileVersion` says when a cached name and avatar
 * have gone stale.
 */
export interface StoredSender {
  kind: 'USER' | 'CHANNEL';
  id: Hex;
  displayName: string;
  avatarMediaId: Hex | null;
  profileVersion: number;
}
